import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import Drawer from "../../components/Drawer";
import Breadcrumbs from "../../components/Breadcrumbs";
import ExpiryField from "../../components/ExpiryField";
import { useApi } from "../../hooks/useApi";
import {
  getFirm,
  getFirmStats,
  suspendFirm,
  reactivateFirm,
  resetFirmAdminPassword,
  updateFirmAdmin,
  updateFirmSubscription,
  recordFirmPayment,
  listAddons,
  toggleFirmAddon,
} from "../../api/superAdminService";
import { formatBytes, formatDate, isoPlusDays, isoPlusMonths, isoPlusYears, todayKeyIst } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { showCredentials } from "../../utils/credentials";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function FirmDetailPage() {
  const { id } = useParams();
  const firmRes = useApi(() => getFirm(id), [id]);
  const statsRes = useApi(() => getFirmStats(id), [id]);
  const addonsRes = useApi(() => listAddons(), []);
  const [busy, setBusy] = useState(false);
  const [seatsDrawer, setSeatsDrawer] = useState(false);
  const [paymentDrawer, setPaymentDrawer] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);

  const firm = firmRes.data?.data;
  const stats = statsRes.data?.data;
  const sub = firm?.subscription;

  if (firmRes.loading || !firm) return <LoadingBlock rows={8} />;

  const toggleStatus = async () => {
    const suspendIt = firm.status === "active";
    if (!window.confirm(suspendIt
      ? `Suspend ${firm.name}? Every user of this firm (including their customers) loses API access until reactivated.`
      : `Reactivate ${firm.name}?`)) return;
    setBusy(true);
    try {
      await (suspendIt ? suspendFirm(firm.id) : reactivateFirm(firm.id));
      showToast("success", suspendIt ? "Firm suspended" : "Firm reactivated");
      firmRes.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const onToggleAddon = async (addon, currentlyActive, currentQty) => {
    const payload = { status: currentlyActive ? "inactive" : "active" };
    // Per-unit addons (e.g. Extra Storage per GB) are prepaid: ask how many
    // units the firm paid for when enabling or adjusting.
    if (!currentlyActive && addon.unit_label) {
      const qty = window.prompt(`How many ${addon.unit_label} did the firm pay for?`, String(currentQty || 5));
      if (qty === null) return;
      const n = parseInt(qty, 10);
      if (!n || n < 1) { showToast("error", "Enter a whole number of " + addon.unit_label); return; }
      payload.quantity = n;
    }
    setBusy(true);
    try {
      await toggleFirmAddon(firm.id, addon.id, payload);
      showToast("success", `${addon.name} ${currentlyActive ? "disabled" : "enabled"}`);
      firmRes.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const onEditAddonQty = async (addon, currentQty) => {
    const qty = window.prompt(`New prepaid ${addon.unit_label} amount for ${addon.name}:`, String(currentQty || 1));
    if (qty === null) return;
    const n = parseInt(qty, 10);
    if (!n || n < 1) { showToast("error", "Enter a whole number of " + addon.unit_label); return; }
    setBusy(true);
    try {
      await toggleFirmAddon(firm.id, addon.id, { status: "active", quantity: n });
      showToast("success", `${addon.name} set to ${n} ${addon.unit_label}`);
      firmRes.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const onResetAdminPassword = async (admin) => {
    if (!window.confirm(`Reset the password for ${admin.name} (${admin.username})?`)) return;
    try {
      const res = await resetFirmAdminPassword(firm.id, admin.id);
      showCredentials(res);
    } catch (err) {
      showApiError(err);
    }
  };

  const activePivots = new Map(
    (firm.addons || [])
      .filter((a) => a.pivot?.status === "active")
      .map((a) => [a.id, a.pivot]),
  );

  return (
    <>
      <Breadcrumbs items={[{ label: "Firms", to: "/super/firms" }, { label: firm.name }]} />
      <div className="page-head">
        <div>
          <h1 className="page-title">{firm.name}</h1>
          <div className="page-sub">
            {firm.status === "active"
              ? <span className="badge badge-success">Active</span>
              : <span className="badge badge-danger">Suspended</span>}
            {firm.contact_name && <> · {firm.contact_name}</>}
            {firm.contact_phone && <> · {firm.contact_phone}</>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" disabled={busy} onClick={toggleStatus}
            style={firm.status === "active" ? { color: "var(--danger)" } : undefined}>
            <Icon name={firm.status === "active" ? "log-out" : "check"} size={14} />
            {firm.status === "active" ? "Suspend" : "Reactivate"}
          </button>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head"><div className="card-title">Subscription</div></div>
          <div className="card-body">
            {sub ? (
              <>
                <Row label="Status">
                  <span className="row gap-sm">
                    {sub.is_trial && <span className="badge badge-purple">Trial</span>}
                    <span className={`badge ${sub.status === "active" ? "badge-success" : sub.status === "grace" ? "badge-warning" : "badge-danger"}`}>{sub.status}</span>
                  </span>
                </Row>
                <Row label="Seats">{sub.seats} paid · {firm.seat_usage} used</Row>
                <Row label={sub.is_trial ? "Amount" : "Yearly amount"}>{sub.is_trial ? "Free trial" : inr(sub.yearly_amount)}</Row>
                <Row label="Period">{formatDate(sub.period_start)} → {formatDate(sub.period_end)}</Row>
                {sub.grace_until && <Row label="Grace until">{formatDate(sub.grace_until)}</Row>}
                <div className="row gap-sm" style={{ marginTop: 12 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setSeatsDrawer(true)}>
                    <Icon name="edit" size={13} /> Seats / subscription
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => setPaymentDrawer(true)}>
                    <Icon name="plus" size={13} /> Record payment
                  </button>
                </div>
              </>
            ) : (
              <div className="t-muted">No subscription yet.</div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Usage</div></div>
          <div className="card-body">
            {stats ? (
              <>
                <Row label="Clients">{stats.clients}</Row>
                <Row label="Employees">{stats.employees}</Row>
                <Row label="Admins">{stats.admins}</Row>
                <Row label="Customer logins">{stats.customers}</Row>
                <Row label="Open tasks">{stats.open_tasks}</Row>
                {firm.storage && (
                  <Row label="Storage (pooled)">{formatBytes(firm.storage.used_bytes)} / {firm.storage.quota_gb} GB</Row>
                )}
              </>
            ) : <LoadingBlock rows={4} />}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Addons</div></div>
          <div className="card-body">
            {(addonsRes.data?.data || []).map((addon) => {
              const pivot = activePivots.get(addon.id);
              const active = !!pivot;
              const qty = pivot?.quantity || 1;
              return (
                <div key={addon.id} className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <div className="t-primary">
                      {addon.name}
                      {active && addon.unit_label && <span className="badge badge-info" style={{ marginLeft: 6 }}>{qty} {addon.unit_label}</span>}
                    </div>
                    <div className="t-muted text-xs">
                      {inr(addon.monthly_price)}/month{addon.unit_label ? ` per ${addon.unit_label}` : ""} (billed yearly)
                    </div>
                  </div>
                  <div className="row gap-sm">
                    {active && addon.unit_label && (
                      <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onEditAddonQty(addon, qty)}>
                        Change {addon.unit_label}
                      </button>
                    )}
                    <button
                      className={`btn btn-sm ${active ? "btn-secondary" : "btn-primary"}`}
                      disabled={busy}
                      onClick={() => onToggleAddon(addon, active, qty)}
                    >
                      {active ? "Disable" : "Enable"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Firm admins</div></div>
          <div className="card-body">
            {(firm.admins || []).map((a) => (
              <div key={a.id} className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
                <div>
                  <div className="t-primary">{a.name} {!a.status && <span className="badge badge-neutral">disabled</span>}</div>
                  <div className="t-muted text-xs t-mono">{a.username}</div>
                </div>
                <div className="row gap-sm">
                  <button className="icon-btn" title="Edit name / username" onClick={() => setEditAdmin(a)}>
                    <Icon name="edit" size={13} />
                  </button>
                  <button className="icon-btn" title="Reset password (one-time temp password)" onClick={() => onResetAdminPassword(a)}>
                    <Icon name="key" size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head">
          <div>
            <div className="card-title">Employees</div>
            <div className="card-sub">{(firm.employees || []).length} total · {(firm.employees || []).filter((e) => !e.left_at).length} active</div>
          </div>
        </div>
        <div className="card-body no-pad">
          {!(firm.employees || []).length ? (
            <div className="t-muted" style={{ padding: 16 }}>No employees yet.</div>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Name</th><th>Employee ID</th><th>Department</th><th>Username</th><th>Status</th></tr>
              </thead>
              <tbody>
                {firm.employees.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <div className="t-primary">{e.name}</div>
                      {e.designation && <div className="t-muted text-xs">{e.designation}</div>}
                    </td>
                    <td className="t-mono">{e.employee_id}</td>
                    <td>{e.department?.name || "—"}</td>
                    <td className="t-mono">{e.user?.username || "—"}</td>
                    <td>
                      {e.left_at
                        ? <span className="badge badge-neutral">Left</span>
                        : (e.user?.status
                            ? <span className="badge badge-success">Active</span>
                            : <span className="badge badge-warning">Disabled</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><div className="card-title">Payment history</div></div>
        <div className="card-body no-pad">
          {!sub?.payments?.length ? (
            <div className="t-muted" style={{ padding: 16 }}>No payments recorded yet.</div>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Date</th><th className="t-num">Amount</th><th>Method</th><th>Reference</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {sub.payments.map((p) => (
                  <tr key={p.id}>
                    <td>{formatDate(p.paid_at)}</td>
                    <td className="t-num">{inr(p.amount)}</td>
                    <td>{p.method}</td>
                    <td className="t-mono">{p.reference || "—"}</td>
                    <td className="t-muted">{p.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <SeatsDrawer key={seatsDrawer ? `seats-${sub?.period_end}-${sub?.seats}-${sub?.is_trial}` : "seats-closed"}
        open={seatsDrawer} onClose={() => setSeatsDrawer(false)} firm={firm}
        onSaved={() => { setSeatsDrawer(false); firmRes.reload(); }} />
      <PaymentDrawer open={paymentDrawer} onClose={() => setPaymentDrawer(false)} firm={firm}
        onSaved={() => { setPaymentDrawer(false); firmRes.reload(); }} />
      <EditFirmAdminDrawer firmId={firm.id} admin={editAdmin} onClose={() => setEditAdmin(null)}
        onSaved={() => { setEditAdmin(null); firmRes.reload(); }} />
    </>
  );
}

function EditFirmAdminDrawer({ firmId, admin, onClose, onSaved }) {
  const open = !!admin;
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!admin) return;
    setName(admin.name || "");
    setUsername(admin.username || "");
    setErrors({});
  }, [admin]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      await updateFirmAdmin(firmId, admin.id, { name: name.trim(), username: username.trim() });
      showToast("success", "Admin updated");
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Edit firm admin"
      subtitle="Change the display name or login username."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !name.trim() || !username.trim()}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">Username</label>
        <input className="input" value={username} onChange={(e) => setUsername(e.target.value)}
          autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        <div className="field-hint">Login username. Lowercase letters, digits, and . _ - only.</div>
        {errors.username?.[0] && <div className="field-error">{errors.username[0]}</div>}
      </div>
    </Drawer>
  );
}

function Row({ label, children }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
      <span className="t-muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function SeatsDrawer({ open, onClose, firm, onSaved }) {
  const sub = firm.subscription;
  const currentEnd = sub?.period_end ? String(sub.period_end).slice(0, 10) : isoPlusYears(1);
  const [seats, setSeats] = useState(sub?.seats ?? 5);
  const [isTrial, setIsTrial] = useState(!!sub?.is_trial);
  // Pre-fill the current expiry (if still in the future) so a seats-only save
  // keeps the same date; fall back to a fresh year for an already-expired firm.
  const [periodEnd, setPeriodEnd] = useState(currentEnd > todayKeyIst() ? currentEnd : isoPlusYears(1));
  const [saving, setSaving] = useState(false);

  // Extend presets build on the current period_end (preserving remaining
  // time); the trial preset is a fresh 7-day window from today.
  const presets = [
    { label: "7-day trial", iso: isoPlusDays(7) },
    { label: "+1 month", iso: isoPlusMonths(1, currentEnd) },
    { label: "+1 year", iso: isoPlusYears(1, currentEnd) },
    { label: "+2 years", iso: isoPlusYears(2, currentEnd) },
  ];

  const submit = async () => {
    setSaving(true);
    try {
      await updateFirmSubscription(firm.id, {
        seats: Number(seats),
        period_end: periodEnd,
        is_trial: isTrial,
      });
      showToast("success", "Subscription updated");
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Seats / subscription"
      subtitle="Set seats and the validity date — extend, reduce, start a trial, or convert a trial to paid."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !seats || !periodEnd}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }>
      <div className="field">
        <label className="field-label">Paid seats</label>
        <input className="input" type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
        <div className="field-hint">Currently used: {firm.seat_usage}. Cannot go below usage.</div>
      </div>
      <div className="field">
        <label className="row gap-sm" style={{ cursor: "pointer" }}>
          <input type="checkbox" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />
          Trial (free — ₹0; expires with no grace period)
        </label>
        <div className="field-hint">Untick and set a future date to convert a trial to a paid subscription.</div>
      </div>
      <ExpiryField
        isTrial={isTrial}
        value={periodEnd}
        onChange={setPeriodEnd}
        presets={presets}
        label="Valid until"
      />
      <div className="field-hint">Current period ends {formatDate(sub?.period_end)}. Saving reactivates a suspended firm and clears any grace.</div>
    </Drawer>
  );
}

function PaymentDrawer({ open, onClose, firm, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ amount: "", paid_at: today, method: "bank_transfer", reference: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    try {
      await recordFirmPayment(firm.id, { ...form, amount: Number(form.amount) });
      showToast("success", "Payment recorded");
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Record payment"
      subtitle={`Yearly amount due: ${inr(firm.subscription?.yearly_amount)}`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.amount || !form.paid_at}>
            {saving ? "Saving…" : "Record"}
          </button>
        </>
      }>
      <div className="field">
        <label className="field-label">Amount (₹)</label>
        <input className="input" type="number" min={1} value={form.amount} onChange={(e) => set({ amount: e.target.value })} autoFocus />
      </div>
      <div className="field">
        <label className="field-label">Paid on</label>
        <input className="input" type="date" value={form.paid_at} onChange={(e) => set({ paid_at: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label">Method</label>
        <select className="input" value={form.method} onChange={(e) => set({ method: e.target.value })}>
          <option value="bank_transfer">Bank transfer</option>
          <option value="upi">UPI</option>
          <option value="cash">Cash</option>
          <option value="cheque">Cheque</option>
        </select>
      </div>
      <div className="field">
        <label className="field-label">Reference (optional)</label>
        <input className="input" value={form.reference} onChange={(e) => set({ reference: e.target.value })} placeholder="UTR / cheque no." />
      </div>
      <div className="field">
        <label className="field-label">Notes (optional)</label>
        <textarea className="input" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
      </div>
    </Drawer>
  );
}
